import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { imgSrc, fmtPrice, FALLBACK_HERO, mergeContent } from "../lib/api";
import { useSite } from "../lib/SiteContext";

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay, className = "" }) {
  return (
    <span className="block overflow-hidden pb-[0.25em] -mb-[0.12em] pr-[0.35em]">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.2, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero({ artwork }) {
  const { settings } = useSite();
  const c = mergeContent(settings);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const imgX = useTransform(sx, [-0.5, 0.5], [-16, 16]);
  const imgY = useTransform(sy, [-0.5, 0.5], [-12, 12]);
  const rot = useTransform(sx, [-0.5, 0.5], [-1.4, 1.4]);

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const img = artwork ? imgSrc(artwork.image_path) || FALLBACK_HERO : FALLBACK_HERO;

  return (
    <section
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative min-h-[100svh] flex items-center overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 72% 18%, rgba(200,134,42,0.10), transparent 65%), radial-gradient(ellipse 55% 50% at 10% 90%, rgba(107,31,31,0.32), transparent 62%), radial-gradient(ellipse 40% 35% at 85% 70%, rgba(179,84,46,0.14), transparent 60%), radial-gradient(ellipse 30% 30% at 50% 50%, rgba(107,31,31,0.10), transparent 65%)",
        }}
      />
      <div className="relative mx-auto max-w-[1440px] w-full px-6 md:px-10 pt-32 md:pt-48 pb-24 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 lg:gap-10 items-center">
        <div>
          {c.hero_eyebrow && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.1 }}
              className="flex items-center gap-4 mb-8 md:mb-10"
            >
              <span className="h-px w-12 rule-flare" />
              <p className="eyebrow !text-ochre/90">{c.hero_eyebrow}</p>
            </motion.div>
          )}
          <h1 className="font-script leading-[1.04] text-[clamp(3.4rem,9.5vw,8.5rem)] tracking-[0.02em]">
            {c.hero_headline_top && <MaskedLine delay={0.2} className="text-bone">{c.hero_headline_top}</MaskedLine>}
            {c.hero_headline_bottom && (
              <MaskedLine delay={0.34} className="metal-red pr-[0.15em]">
                {c.hero_headline_bottom}
              </MaskedLine>
            )}
          </h1>
          {c.hero_subline && (
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.7 }}
              className="mt-8 md:mt-10 max-w-md text-sm md:text-base leading-relaxed text-mute"
            >
              {c.hero_subline}
            </motion.p>
          )}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-3 font-meta text-[9px] md:text-[10px] uppercase tracking-[0.3em]"
          >
            <span className="border border-oxblood/60 px-4 py-2 text-bone/80">
              Original Works
            </span>
            <span className="border border-oxblood/60 px-4 py-2 text-bone/80">
              Limited Prints
            </span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.3, ease: EASE, delay: 0.55 }}
          className="relative mx-auto w-fit"
        >
          <div
            aria-hidden="true"
            className="absolute -top-5 -left-5 h-full w-full border border-copper/40"
          />
          <motion.div
            style={{ x: imgX, y: imgY, rotate: rot }}
            className="relative bg-panel border border-line p-3"
          >
            <div className="overflow-hidden">
              <img
                src={img}
                alt={artwork?.title || "Featured artwork"}
                className="w-[min(420px,78vw)] aspect-[3/4] object-cover animate-float-slow"
                data-testid="hero-artwork-image"
              />
            </div>
          </motion.div>
          {artwork && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 1.1 }}
              className="mt-6 flex items-end justify-between gap-6"
            >
              <div>
                <p className="font-display italic text-lg text-bone" data-testid="hero-artwork-title">
                  {artwork.title}
                </p>
                <p className="font-meta text-[10px] uppercase tracking-[0.2em] text-mute mt-1">
                  {[artwork.medium, artwork.dimensions].filter(Boolean).join(" · ")}
                </p>
              </div>
              {!!artwork.price && (
                <p className="font-meta text-sm text-gold" data-testid="hero-artwork-price">
                  {fmtPrice(artwork.price)}
                </p>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-10 flex items-center gap-4"
      >
        <span className="font-meta text-[9px] uppercase tracking-[0.35em] text-mute">
          Scroll
        </span>
        <span className="block h-12 w-px bg-gradient-to-b from-copper to-transparent animate-scroll-pulse origin-top" />
      </motion.div>
    </section>
  );
}
